import sr_rs from "config/onboarding/sr_rs.json";
import en from "config/onboarding/en.json";
import Sitemap from "routes/sitemap";

/**
 * Onboarding (welcome dialog + guided tour) state and content.
 *
 * Completion is kept in localStorage for now. Bump ONBOARDING_VERSION when the tour
 * changes enough that every user should see it again.
 */
export const ONBOARDING_VERSION = 1;

/** Query param that overrides the stored state: "true" marks the tour as seen, "false" resets it. */
export const ONBOARDING_PARAM = "onboarding_version_seen";

const STORAGE_KEY = "onboarding_version_seen";
const SESSION_KEY = "onboarding_postponed";
const FALLBACK_LANGUAGE = "sr_rs";

// Content per language code. Will be replaced by the API/CMS later, keep the same shape.
const CONTENT = { sr_rs, en };

const safeStorage = (storage, action) => {
    try {
        return action(window[storage]);
    } catch {
        return null;
    }
};

export const isOnboardingSeen = () => Number(safeStorage("localStorage", (s) => s.getItem(STORAGE_KEY))) >= ONBOARDING_VERSION;

export const markOnboardingSeen = () => safeStorage("localStorage", (s) => s.setItem(STORAGE_KEY, String(ONBOARDING_VERSION)));

export const resetOnboarding = () => {
    safeStorage("localStorage", (s) => s.removeItem(STORAGE_KEY));
    safeStorage("sessionStorage", (s) => s.removeItem(SESSION_KEY));
};

/** Router state flag the <Onboarding /> component watches for. */
export const ONBOARDING_START_STATE = "startOnboardingTour";

/**
 * Go to the home page and start the guided tour there (e.g. from the manual page), whether it was seen or not.
 *
 * @param {function} navigate From useNavigate().
 */
export const startOnboardingTour = (navigate) => navigate(Sitemap.home, { state: { [ONBOARDING_START_STATE]: true } });

/** "Later": hide the welcome dialog until the browser session ends. */
export const isOnboardingPostponed = () => safeStorage("sessionStorage", (s) => s.getItem(SESSION_KEY)) === "1";

export const postponeOnboarding = () => safeStorage("sessionStorage", (s) => s.setItem(SESSION_KEY, "1"));

/**
 * Apply the ?onboarding_version_seen= override, if present.
 *
 * @param {URLSearchParams} searchParams
 * @return {boolean} True if the param was present (and should be removed from the URL).
 */
export const applyOnboardingParam = (searchParams) => {
    const value = searchParams.get(ONBOARDING_PARAM);
    if (value === "true") markOnboardingSeen();
    else if (value === "false") resetOnboarding();
    return value !== null;
};

/**
 * Get the onboarding content for a language: exact code, then base language ("en_us" -> "en"), then Serbian.
 *
 * @param {string} language
 * @return {{welcome: {}, buttons: {}, steps: {id: string, title: string, description: string}[], finish: {}}}
 */
export const getOnboardingContent = (language) => {
    const code = String(language ?? "").toLowerCase();
    const base = Object.keys(CONTENT).find((key) => code.startsWith(`${key}_`));
    return CONTENT[code] ?? CONTENT[base] ?? CONTENT[FALLBACK_LANGUAGE];
};
